import { Autocomplete, TextField, MenuItem, Typography, Checkbox, FormControlLabel, Chip, Card, CardContent, Box, Grid, Stack, IconButton, Tooltip, Button, Avatar, Tabs, Tab } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useEffect, useRef } from "react";
import { verifyDomainAction } from "../../Actions/TenantActions";
import { MyVerifiedIcon, MyPersonIcon } from "../../Assets/svg/MyIcons";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

const baseDomain = "netcluesdemo.com";

const TenantInformationStep = ({ tenantId = null, formik, handleEditing, isEditing, stateData = [], countryData = [], tenantCurrencies = [], logoRemoved, setLogoRemoved }) => {
	const [buttonColor, setButtonColor] = useState(() => tenantId ? "success" : "");
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const [responseMessage, setResponseMessage] = useState("");
	const [previousDomain, setPreviousDomain] = useState("");
	const [isManuallyEdited, setIsManuallyEdited] = useState(false);
	const [enableWhitelist, setEnableWhitelist] = useState(false);
	const [whitelistIps, setWhitelistIps] = useState([]);
	const [ipInputValue, setIpInputValue] = useState("");
	const [ipError, setIpError] = useState("");
	const [brandingLogoPreview, setBrandingLogoPreview] = useState(formik.values.brandingLogo || "");
	const [signatureTab, setSignatureTab] = useState(0);
	const signatureContainerRef = useRef(null);
	const signatureContentRef = useRef(null);
	const [signatureScale, setSignatureScale] = useState(1);

	const isValidIPv4 = (ip) => {
		const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
		return ipv4Regex.test(ip);
	};

	const handleBrandingLogoChange = (e) => {
		const file = e.target.files?.[0];
		if (file) {
			const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
			if (!validTypes.includes(file.type)) {
				formik.setFieldError("brandingLogo", "Please upload a valid image file (JPEG, PNG, GIF, or WebP)");
				return;
			}
			const maxSize = 5 * 1024 * 1024;
			if (file.size > maxSize) {
				formik.setFieldError("brandingLogo", "File size must be less than 5MB");
				return;
			}
			const reader = new FileReader();
			reader.onloadend = () => {
				setBrandingLogoPreview(reader.result);
				formik.setFieldValue("brandingLogo", file);
				formik.setFieldError("brandingLogo", "");
			};
			reader.readAsDataURL(file);
		}
	};

	const handleRemoveBrandingLogo = () => {
		setBrandingLogoPreview("");
		formik.setFieldValue("brandingLogo", "");
		formik.setFieldError("brandingLogo", "");
		if (setLogoRemoved) {
			setLogoRemoved(true);
		}
	};

	const handleNameChange = (e) => {
		formik.setFieldValue(e.target.name, e.target.value.replace(/\s/g, ''));
		formik.setFieldTouched(e.target.name, true, false);
	};

	const handleCompanyNameChange = (e) => {
		formik.setFieldValue(e.target.name, e.target.value.replace(/^\s+/, ''));
		formik.setFieldTouched(e.target.name, true, false);
	};

	const handleEmailChange = (e) => {
		formik.setFieldValue(e.target.name, e.target.value.replace(/\s/g, ''));
		formik.setFieldTouched(e.target.name, true, false);
	};

	const handleZipCodeChange = (e) => {
		const newValue = e.target.value.replace(/[^a-zA-Z0-9\-\u2013\u2014]/g, '');
		formik.setFieldValue(e.target.name, newValue);
		formik.setFieldTouched(e.target.name, true, false);
	};

	const handleAddressChange = (e) => {
		formik.handleChange(e);
		formik.setFieldTouched(e.target.name, true, false);
	};

	const generateFullDomain = (subdomain) => {
		if (!subdomain) return "";
		const cleanSubdomain = subdomain.trim().replace(/\s+/g, "").replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
		return cleanSubdomain ? `${cleanSubdomain}.${baseDomain}` : "";
	};

	const extractSubdomain = (fullDomain) => {
		if (!fullDomain) return "";
		return fullDomain.replace(`.${baseDomain}`, "").trim();
	};

	const isValidSubdomain = (subdomain) => {
		const subdomainRegex = /^[a-z0-9-]+$/;
		return subdomainRegex.test(subdomain);
	};

	const verifyDomainURL = async () => {
		try {
			const domainUrl = formik.values.domainUrl;
			if (!domainUrl) {
				formik.setFieldValue("isVerified", false);
				setResponseMessage("Please enter a domain");
				setButtonColor("error");
				return;
			}
			const response = await verifyDomainAction({ domainUrl });
			if (response?.data?.status === 200) {
				setButtonColor("error");
				setResponseMessage("Domain already exists");
				formik.setFieldValue("isVerified", false);
				setIsButtonDisabled(false);
			} else if (response?.data?.status === 202) {
				setButtonColor("success");
				setResponseMessage("Domain verified successfully");
				formik.setFieldValue("isVerified", true);
				setIsButtonDisabled(true);
			}
			setTimeout(() => setResponseMessage(""), 3000);
		} catch (error) {
			setButtonColor("error");
			setResponseMessage(error?.response?.data?.message || "Verification failed. Try again.");
			formik.setFieldValue("isVerified", false);
			setIsButtonDisabled(false);
		}
	};

	const handleEditClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setPreviousDomain(formik.values.domainUrl);
		const subdomain = extractSubdomain(formik.values.domainUrl);
		formik.setFieldValue("domainUrl", subdomain);
		handleEditing(true);
		setButtonColor("");
		formik.setFieldValue("isVerified", false);
		setIsButtonDisabled(false);
		setResponseMessage("");
	};

	const handleSaveDomainUrl = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const subdomain = formik.values.domainUrl.trim();
		if (!subdomain) {
			formik.setFieldError("domainUrl", "Please enter a subdomain");
			return;
		}
		if (!isValidSubdomain(subdomain)) {
			formik.setFieldError("domainUrl", "Subdomain can only contain lowercase letters, numbers, and hyphens");
			return;
		}
		const fullDomain = generateFullDomain(subdomain);
		formik.setFieldError("domainUrl", "");
		formik.setFieldValue("domainUrl", fullDomain, false);
		formik.setFieldValue("isVerified", false, false);
		formik.setFieldTouched("domainUrl", false, false);
		setButtonColor("");
		setIsButtonDisabled(false);
		setResponseMessage("");
		setIsManuallyEdited(true);
		handleEditing(false);
	};

	const handleCancelEdit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		formik.setFieldValue("domainUrl", previousDomain || generateFullDomain(formik.values.companyName));
		formik.setFieldError("domainUrl", "");
		handleEditing(false);
		setResponseMessage("");
	};

	const handleWhitelistToggle = (e) => {
		const checked = e.target.checked;
		setEnableWhitelist(checked);
		if (!checked) {
			setWhitelistIps([]);
			setIpInputValue("");
			setIpError("");
			formik.setFieldValue("whitelistIps", []);
			formik.setFieldError("whitelistIps", "");
		} else {
			formik.setFieldError("whitelistIps", "At least 1 valid IP address is required when whitelist is enabled");
		}
	};

	useEffect(() => {
		if (signatureTab === 0 && formik.values.signatureBody) {
			const contentNode = signatureContentRef.current;
			let innerNode = null;
			
			// 1. Setup Shadow DOM to protect signature styling from global CSS
			if (contentNode) {
				let shadow = contentNode.shadowRoot;
				if (!shadow) {
					shadow = contentNode.attachShadow({ mode: 'open' });
				}
				// Wrap signature in a measurable inline-block div
				shadow.innerHTML = `<div id="shadow-inner" style="display:inline-block; margin:0; padding:0;">${formik.values.signatureBody}</div>`;
				innerNode = shadow.getElementById('shadow-inner');
			}

			// 2. Auto-scaling logic (ResizeObserver pattern implemented via resize events)
			const calculateScale = () => {
				if (signatureContainerRef.current && signatureContentRef.current && innerNode) {
					const containerWidth = signatureContainerRef.current.offsetWidth;
					
					// Temporarily reset transform to get accurate physical width
					const prevTransform = signatureContentRef.current.style.transform;
					signatureContentRef.current.style.transform = 'none';
					
					const contentWidth = innerNode.offsetWidth;
					signatureContentRef.current.style.transform = prevTransform;

					if (contentWidth > 0 && containerWidth > 0) {
						let scale = containerWidth / contentWidth;
						if (scale > 1) scale = 1; // Never scale up, only shrink down
						setSignatureScale(scale);
					}
				}
			};

			calculateScale();
			
			// Delay slightly in case images load
			const timerId = setTimeout(calculateScale, 100);
			const timerId2 = setTimeout(calculateScale, 500);

			window.addEventListener('resize', calculateScale);
			return () => {
				window.removeEventListener('resize', calculateScale);
				clearTimeout(timerId);
				clearTimeout(timerId2);
			};
		}
	}, [signatureTab, formik.values.signatureBody]);

	const addIpToList = (ipToAdd) => {
		const trimmedIp = ipToAdd.trim();
		if (!trimmedIp) return;
		if (isValidIPv4(trimmedIp)) {
			if (!whitelistIps.includes(trimmedIp)) {
				const updatedIps = formik.values.whitelistIps || [];
				updatedIps.push(trimmedIp);
				setWhitelistIps(updatedIps);
				formik.setFieldValue("whitelistIps", updatedIps);
				setIpError("");
				formik.setFieldError("whitelistIps", "");
			} else {
				setIpError(`IP address ${trimmedIp} already exists`);
				setTimeout(() => setIpError(""), 3000);
			}
		} else {
			setIpError(`Invalid IP address format: ${trimmedIp}`);
			setTimeout(() => setIpError(""), 3000);
		}
	};

	const handleIpKeyDown = (event) => {
		if (event.key === " " || event.key === "Tab") {
			event.preventDefault();
			if (ipInputValue.trim()) {
				addIpToList(ipInputValue);
				setIpInputValue("");
			}
		}
	};

	const handleIpInputBlur = () => {
		if (ipInputValue.trim()) {
			addIpToList(ipInputValue);
			setIpInputValue("");
		}
	};

	const handleIpChange = (event, newValue) => {
		const currentIps = formik.values.whitelistIps || [];
		const isRemoval = newValue.length < currentIps.length;
		const isAddition = newValue.length > currentIps.length;
		if (isRemoval) {
			setWhitelistIps(newValue);
			formik.setFieldValue("whitelistIps", newValue);
			setIpError("");
			formik.setFieldError("whitelistIps", "");
			if (enableWhitelist && newValue.length === 0) {
				formik.setFieldError("whitelistIps", "At least 1 valid IP address is required when whitelist is enabled");
			}
			return;
		}
		if (isAddition) {
			const lastValue = newValue[newValue.length - 1];
			const trimmedIp = lastValue ? lastValue.trim() : "";
			if (trimmedIp) {
				if (isValidIPv4(trimmedIp)) {
					const isDuplicate = newValue.filter((ip) => ip === trimmedIp).length > 1;
					if (!isDuplicate) {
						setWhitelistIps(newValue);
						formik.setFieldValue("whitelistIps", newValue);
						setIpError("");
						formik.setFieldError("whitelistIps", "");
					} else {
						setIpError(`IP address ${trimmedIp} already exists`);
						setTimeout(() => setIpError(""), 3000);
					}
				} else {
					setIpError(`Invalid IP address format: ${trimmedIp}`);
					setTimeout(() => setIpError(""), 3000);
				}
			}
		} else {
			setWhitelistIps(newValue);
			formik.setFieldValue("whitelistIps", newValue);
			setIpError("");
			formik.setFieldError("whitelistIps", "");
		}
	};

	useEffect(() => {
		if (enableWhitelist) {
			if (whitelistIps.length === 0) {
				formik.setFieldError("whitelistIps", "At least 1 valid IP address is required when whitelist is enabled");
			} else {
				formik.setFieldError("whitelistIps", "");
			}
		}
	}, [whitelistIps, enableWhitelist]);

	useEffect(() => {
		// Update branding logo preview when formik value changes (e.g., when loading tenant data)
		if (formik.values.brandingLogo) {
			// If it's a File object, read it as data URL
			if (formik.values.brandingLogo instanceof File) {
				const reader = new FileReader();
				reader.onloadend = () => {
					setBrandingLogoPreview(reader.result);
				};
				reader.readAsDataURL(formik.values.brandingLogo);
			} else if (typeof formik.values.brandingLogo === 'string') {
				// If it's a string (filename), construct the full URL with tenant domain
				const tenantDomain = formik.values.domainUrl || "default";
				const logoUrl = `${process.env.REACT_APP_MEDIA_URL}/${tenantDomain}/${formik.values.brandingLogo}`;
				setBrandingLogoPreview(logoUrl);
			}
		} else {
			setBrandingLogoPreview("");
		}
	}, [formik.values.brandingLogo, formik.values.domainUrl]);

	useEffect(() => {
		if (!isEditing && formik.values.companyName && !formik.values.isVerified && !isManuallyEdited && !tenantId) {
			const generatedDomain = generateFullDomain(formik.values.companyName);
			formik.setFieldValue("domainUrl", generatedDomain);
			setButtonColor("");
			formik.setFieldValue("isVerified", false);
			setIsButtonDisabled(false);
			setResponseMessage("");
		}
	}, [formik.values.companyName, isEditing, formik.values.isVerified, isManuallyEdited, tenantId]);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
			<Card sx={{ width: "100%", display: "flex", borderRadius: 0, boxShadow: "0 20px 60px rgba(0,0,0,0.1)", overflow: "hidden" }}>
				<CardContent sx={{ p: 3, flex: 1 }}>
					<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
						<Box sx={{ bgcolor: "var(--bs-primary)", p: 1, borderRadius: 1, display: "flex" }}>
							<MyPersonIcon sx={{ fontSize: 24, color: "white" }} />
						</Box>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>Company Details</Typography>
					</Stack>
					<Grid container spacing={3} pb={1}>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>Company Name<span className="text-danger ms-1">*</span></Typography>
							<TextField placeholder="Enter company name" name="companyName" size="small" value={formik.values.companyName} onChange={handleCompanyNameChange} onBlur={(e) => { formik.setFieldValue("companyName", e.target.value.trim()); formik.handleBlur(e); }} error={formik.touched.companyName && Boolean(formik.errors.companyName)} helperText={formik.touched.companyName && formik.errors.companyName} disabled={Boolean(tenantId)} fullWidth />
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>Domain <span className="text-danger ms-1">*</span></Typography>
							<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
								<Box sx={{ flex: 1 }}>
									<TextField name="domainUrl" type="text" size="small" value={formik.values.domainUrl} onChange={(e) => { if (isEditing) { const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""); formik.setFieldValue("domainUrl", value); } }} onBlur={formik.handleBlur} error={formik.touched.domainUrl && Boolean(formik.errors.domainUrl)} helperText={formik.touched.domainUrl && formik.errors.domainUrl} fullWidth disabled={!isEditing} placeholder={isEditing ? "Enter subdomain" : ""} InputProps={{ endAdornment: isEditing && (<Typography variant="body2" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>.{baseDomain}</Typography>) }} />
								</Box>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									{isEditing ? (<><Tooltip title="Save"><IconButton className="text-primary border border-primary" type="button" onClick={handleSaveDomainUrl}><SaveIcon fontSize="small" /></IconButton></Tooltip><Tooltip title="Cancel"><IconButton className="text-danger border border-danger" type="button" onClick={handleCancelEdit}><CloseIcon fontSize="small" /></IconButton></Tooltip></>) : (<>{!tenantId && (<Tooltip title="Edit"><span style={{ cursor: (!formik.values.domainUrl || Boolean(tenantId)) ? 'not-allowed' : 'pointer' }}><IconButton className="text-primary border border-primary" type="button" onClick={handleEditClick} disabled={!formik.values.domainUrl || Boolean(tenantId)} sx={{ "&.Mui-disabled": { opacity: 0.5 } }}><EditIcon fontSize="small" /></IconButton></span></Tooltip>)}</>)}
									{!isEditing && (<>{tenantId ? (formik.values.isVerified && (<Tooltip title="Verified"><span style={{ cursor: "not-allowed" }}><IconButton className="text-success border border-success" disabled={true} sx={{ "&.Mui-disabled": { opacity: 0.5 } }}><MyVerifiedIcon fontSize="small" className="text-success" /></IconButton></span></Tooltip>)) : (<Tooltip title={formik.values.isVerified ? "Verified" : "Verify Domain"}><span style={{ cursor: isButtonDisabled ? "not-allowed" : "pointer" }}><IconButton className={buttonColor === "success" || formik.values.isVerified ? "text-success border border-success success" : buttonColor === "error" ? "text-danger border border-danger" : "text-primary border border-primary"} onClick={verifyDomainURL} disabled={isButtonDisabled} sx={{ "&.Mui-disabled": { opacity: 0.5 } }}><MyVerifiedIcon fontSize="small" className={buttonColor === "success" || formik.values.isVerified ? "text-success" : buttonColor === "error" ? "text-danger" : "text-primary"} /></IconButton></span></Tooltip>)}</>)}
								</Box>
							</Box>
							{formik.touched.isVerified && formik.errors.isVerified && !formik.values.isVerified && (<Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>{formik.errors.isVerified}</Typography>)}
							{responseMessage && (<Typography variant="caption" color={buttonColor === "success" ? "success.main" : "error.main"} sx={{ mt: 0.5, display: "block" }}>{responseMessage}</Typography>)}
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>Company Email<span className="text-danger ms-1">*</span></Typography>
							<TextField placeholder="Enter company email" name="companyEmail" size="small" value={formik.values.companyEmail} onChange={handleEmailChange} onBlur={formik.handleBlur} error={formik.touched.companyEmail && Boolean(formik.errors.companyEmail)} helperText={formik.touched.companyEmail && formik.errors.companyEmail} fullWidth />
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>Company Phone Number<span className="text-danger ms-1">*</span></Typography>
							<PhoneInput placeholder="Enter company phone number" country={"us"} value={formik.values.companyPhoneNumber} onChange={(phone) => { formik.setFieldValue("companyPhoneNumber", phone); formik.setFieldTouched("companyPhoneNumber", true, false); }} onBlur={() => formik.setFieldTouched("companyPhoneNumber", true, true)} inputStyle={{ width: "100%", height: "40px", fontSize: "14px", borderColor: formik.touched.companyPhoneNumber && formik.errors.companyPhoneNumber ? "#d32f2f" : undefined }} />
							{formik.touched.companyPhoneNumber && formik.errors.companyPhoneNumber && (
								<Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>{formik.errors.companyPhoneNumber}</Typography>
							)}
						</Grid>
						<Grid item xs={12}>
							<Typography variant="subtitle2" gutterBottom>Company Address<span className="text-danger ms-1">*</span></Typography>
							<TextField placeholder="Enter company address" name="companyAddress" rows={4} multiline fullWidth value={formik.values.companyAddress} onChange={handleAddressChange} onBlur={formik.handleBlur} error={formik.touched.companyAddress && Boolean(formik.errors.companyAddress)} helperText={formik.touched.companyAddress && formik.errors.companyAddress} />
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="subtitle2" gutterBottom>Company Zip Code<span className="text-danger ms-1">*</span></Typography>
							<TextField placeholder="Enter company zip code" type="text" name="companyZipCode" size="small" value={formik.values.companyZipCode} onChange={handleZipCodeChange} onBlur={formik.handleBlur} error={formik.touched.companyZipCode && Boolean(formik.errors.companyZipCode)} helperText={formik.touched.companyZipCode && formik.errors.companyZipCode} fullWidth />
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="subtitle2" gutterBottom>Company Country<span className="text-danger ms-1">*</span></Typography>
							<Autocomplete options={countryData} getOptionLabel={(option) => option?.name || ""} value={countryData.find((option) => option.name === formik.values.companyCountry) || null} onChange={(event, value) => { formik.setFieldValue("companyCountry", value?.name || ""); if (formik.values.companyCountry !== value?.name) { formik.setFieldValue("companyState", ""); } }} onBlur={formik.handleBlur} renderInput={(params) => (<TextField placeholder="Select company country" {...params} size="small" fullWidth error={formik.touched.companyCountry && Boolean(formik.errors.companyCountry)} helperText={formik.touched.companyCountry && formik.errors.companyCountry} />)} />
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="subtitle2" gutterBottom>Company State<span className="text-danger ms-1">*</span></Typography>
							<Autocomplete options={stateData} getOptionLabel={(option) => option?.name || ""} value={stateData.find((option) => option.name === formik.values.companyState) || null} onChange={(event, value) => formik.setFieldValue("companyState", value?.name || "")} onBlur={formik.handleBlur} disabled={!stateData.length || !formik.values.companyCountry} renderInput={(params) => (<TextField placeholder="Select company state" {...params} size="small" fullWidth error={formik.touched.companyState && Boolean(formik.errors.companyState)} helperText={formik.touched.companyState && formik.errors.companyState} />)} />
						</Grid>
						<Grid item xs={12} md={6}>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<Typography variant="subtitle2" gutterBottom>Branding Name</Typography>
									<TextField placeholder="Enter branding name" name="brandingName" size="small" value={formik.values.brandingName || ""} onChange={handleCompanyNameChange} onBlur={formik.handleBlur} error={formik.touched.brandingName && Boolean(formik.errors.brandingName)} helperText={formik.touched.brandingName && formik.errors.brandingName} fullWidth />
								</Grid>
								<Grid item xs={12}>
									<Typography variant="subtitle2" gutterBottom>Branding Logo</Typography>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2, border: "2px dashed #ccc", borderRadius: 1, backgroundColor: "#fafafa", textAlign: "center", cursor: "pointer", transition: "all 0.3s ease", "&:hover": { borderColor: "var(--bs-primary)", backgroundColor: "#f0f7ff" } }} component="label">
										{brandingLogoPreview ? (<Box sx={{ position: "relative", display: "inline-block" }}><Avatar src={brandingLogoPreview} sx={{ width: 100, height: 100, margin: "0 auto", border: "2px solid var(--bs-primary)" }} /><Button size="small" variant="contained" color="error" startIcon={<DeleteIcon />} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveBrandingLogo(); }} sx={{ mt: 1 }}>Remove</Button></Box>) : (<Box><CloudUploadIcon sx={{ fontSize: 48, color: "var(--bs-primary)", mb: 1 }} /><Typography variant="body2" color="textSecondary">Click to upload or drag and drop</Typography><Typography variant="caption" color="textSecondary">PNG, JPG, GIF or WebP (Max 5MB)</Typography></Box>)}
										<input hidden accept="image/*" type="file" onChange={handleBrandingLogoChange} />
									</Box>
									{formik.touched.brandingLogo && formik.errors.brandingLogo && (<Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>{formik.errors.brandingLogo}</Typography>)}
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12} md={6}>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<Typography variant="subtitle2" gutterBottom>Email Signature Title</Typography>
									<TextField placeholder="e.g. Sales Signature" name="signatureTitle" size="small" value={formik.values.signatureTitle || ""} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.signatureTitle && Boolean(formik.errors.signatureTitle)} helperText={formik.touched.signatureTitle && formik.errors.signatureTitle} fullWidth />
								</Grid>
								<Grid item xs={12}>
									<Typography variant="subtitle2" gutterBottom>Email Signature Preview</Typography>
									<Box sx={{ border: "1px solid #ccc", borderRadius: 1, overflow: "hidden" }}>
										<Tabs value={signatureTab} onChange={(e, val) => setSignatureTab(val)} sx={{ borderBottom: "1px solid #ccc", minHeight: "36px" }}>
											<Tab label="Preview" sx={{ minHeight: "36px", py: 0.5 }} />
											<Tab label="HTML Code" sx={{ minHeight: "36px", py: 0.5 }} />
										</Tabs>
										<Box sx={{ p: 2, minHeight: "150px", backgroundColor: "#fff" }}>
											{signatureTab === 0 ? (
												formik.values.signatureBody ? (
													<Box ref={signatureContainerRef} sx={{ width: '100%', minHeight: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
														<Box 
															ref={signatureContentRef}
															sx={{
																transform: `scale(${signatureScale})`,
																transformOrigin: 'center center',
																display: 'inline-block'
															}}
														/>
													</Box>
												) : (
													<Typography variant="body2" color="textSecondary">No content. Switch to HTML Code tab to paste your signature.</Typography>
												)
											) : (
												<TextField 
													name="signatureBody" 
													value={formik.values.signatureBody || ""} 
													onChange={formik.handleChange} 
													placeholder="Paste your HTML signature here..." 
													multiline 
													rows={6} 
													fullWidth 
													InputProps={{ style: { fontFamily: 'monospace', fontSize: '13px' } }}
												/>
											)}
										</Box>
									</Box>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
			<Card sx={{ width: "100%", display: "flex", borderRadius: 0, boxShadow: "0 20px 60px rgba(0,0,0,0.1)", overflow: "hidden" }}>
				<CardContent sx={{ p: 3, flex: 1 }}>
					<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
						<Box sx={{ bgcolor: "var(--bs-primary)", p: 1, borderRadius: 1, display: "flex" }}>
							<MyPersonIcon sx={{ fontSize: 24, color: "white" }} />
						</Box>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>Personal Information</Typography>
					</Stack>
					<Grid container spacing={3} pb={1}>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>First Name<span className="text-danger ms-1">*</span></Typography>
							<TextField placeholder="Enter first name" name="firstName" size="small" value={formik.values.firstName} onChange={handleNameChange} onBlur={formik.handleBlur} error={formik.touched.firstName && Boolean(formik.errors.firstName)} helperText={formik.touched.firstName && formik.errors.firstName} fullWidth />
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>Last Name<span className="text-danger ms-1">*</span></Typography>
							<TextField placeholder="Enter last name" name="lastName" size="small" value={formik.values.lastName} onChange={handleNameChange} onBlur={formik.handleBlur} error={formik.touched.lastName && Boolean(formik.errors.lastName)} helperText={formik.touched.lastName && formik.errors.lastName} fullWidth />
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>Email<span className="text-danger ms-1">*</span></Typography>
							<TextField placeholder="Enter email" name="email" size="small" value={formik.values.email} onChange={handleEmailChange} onBlur={formik.handleBlur} error={formik.touched.email && Boolean(formik.errors.email)} helperText={formik.touched.email && formik.errors.email} fullWidth />
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="subtitle2" gutterBottom>Currency <span className="text-danger ms-1">*</span></Typography>
							<TextField select name="currency" size="small" placeholder="Select Currency" value={formik.values.currency} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.currency && Boolean(formik.errors.currency)} helperText={formik.touched.currency && formik.errors.currency} fullWidth>
								{tenantCurrencies.map((currency) => (<MenuItem key={currency.id} value={currency.id}>{currency?.name || ""} - {currency?.symbol || ""}</MenuItem>))}
							</TextField>
						</Grid>
						{!tenantId && (<Grid item xs={12}><FormControlLabel control={<Checkbox checked={formik.values.isWhiteListIp} onChange={(e) => { formik.setFieldValue("isWhiteListIp", e.target.checked); handleWhitelistToggle(e); }} color="primary" />} label="Whitelist IP" /></Grid>)}
						{!tenantId && formik.values.isWhiteListIp && (<Grid item xs={12}><Typography variant="subtitle2" gutterBottom>IP Addresses <span className="text-danger ms-1">*</span></Typography><Autocomplete multiple freeSolo options={[]} value={formik.values.whitelistIps} onChange={handleIpChange} inputValue={ipInputValue} onInputChange={(event, newInputValue) => { setIpInputValue(newInputValue); }} renderTags={(value, getTagProps) => value.map((option, index) => { const isValid = isValidIPv4(option); return (<Chip variant="outlined" label={option} {...getTagProps({ index })} color={isValid ? "default" : "error"} sx={{ backgroundColor: isValid ? "white" : "#ffebee", borderColor: isValid ? "#d0d0d0" : "#f44336" }} />); })} renderInput={(params) => (<TextField {...params} size="small" placeholder="Enter IP address and press Enter (e.g., 192.168.1.1)" error={Boolean(ipError) || (formik.touched.whitelistIps && Boolean(formik.errors.whitelistIps))} helperText={ipError || (formik.touched.whitelistIps && formik.errors.whitelistIps)} onKeyDown={handleIpKeyDown} onBlur={handleIpInputBlur} />)} /></Grid>)}
					</Grid>
				</CardContent>
			</Card>
		</Box>
	);
};

export default TenantInformationStep;
